import type { AudioPayload } from '../audio/audioUtils';
import { BACKEND_URL, COLLECTIONS, QUERY_LIMITS, STYLE_GROUPS } from '../config/app.config';

// New interfaces for Alpha 2
export interface AlbumInfo {
    album: string;
    genre: string;
    bpm: number;
    /** Time signature numerator: 4 = 4/4, 3 = 3/4 or 6/8. Defaults to 4. */
    pulse_compas: number;
}

export interface SampleInfo {
    file: string;
    album: string;
    genre: string;
    bpm: number;
    token: string;
    duration: number;
    isFill: boolean;
    /** Time signature numerator: 4 = 4/4, 3 = 3/4 or 6/8. Defaults to 4. */
    pulse_compas: number;
}

// Helper to check if we're in development mode
const isDevelopment = import.meta.env.MODE === 'development';

export class QdrantService {
    /**
     * POST to a Qdrant scroll endpoint and return the parsed JSON.
     * Throws on non-OK HTTP responses.
     */
    private async _scrollFetch(collection: string, body: object): Promise<any> {
        const url = `${BACKEND_URL}/collections/${collection}/points/scroll`;
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
    }

    /**
     * Get all distinct genres from librosa_min collection
     * Uses pagination to ensure all genres are retrieved
     */
    async getGenres(): Promise<string[]> {
        try {
            if (isDevelopment) {
                console.log('[Qdrant] Fetching genres');
            }

            let offset: string | null = null;
            const genresSet = new Set<string>();

            do {
                const data = await this._scrollFetch(COLLECTIONS.METADATA, {
                    limit: QUERY_LIMITS.GENRES,
                    offset,
                    with_payload: ['genre'],
                    with_vector: false,
                });

                if (!data.result || !data.result.points) {
                    throw new Error('No data found in Qdrant');
                }

                for (const point of data.result.points) {
                    const payload = point.payload ?? {};
                    if (payload.genre) {
                        genresSet.add(payload.genre.trim().toLowerCase());
                    }
                }

                offset = data.result.next_page_offset ?? null;
            } while (offset !== null);

            const genres = Array.from(genresSet).sort();

            if (isDevelopment) {
                console.log('[Qdrant] Found genres:', genres);
            }

            return genres;
        } catch (error) {
            if (isDevelopment) { console.error('[Qdrant] Error fetching genres:', error); }
            throw new Error(`Failed to fetch genres: ${(error as Error).message}`);
        }
    }

    /**
     * Get all albums for a specific genre
     * Uses pagination to ensure all albums are retrieved
     */
    async getAlbumsByGenre(genre: string): Promise<AlbumInfo[]> {
        try {
            if (isDevelopment) {
                console.log('[Qdrant] Fetching albums for genre:', genre);
            }

            const normalizedGenre = genre.trim().toLowerCase();
            let offset: string | null = null;
            const albumsMap = new Map<string, AlbumInfo>();

            do {
                const data = await this._scrollFetch(COLLECTIONS.METADATA, {
                    filter: {
                        must: [{ key: 'genre', match: { value: normalizedGenre } }],
                    },
                    limit: QUERY_LIMITS.ALBUMS,
                    offset,
                    with_payload: ['album', 'genre', 'bpm', 'pulse_compas'],
                    with_vector: false,
                });

                if (!data.result || !data.result.points) {
                    throw new Error('No albums found for genre');
                }

                for (const point of data.result.points) {
                    const payload = point.payload ?? {};
                    if (payload.album && !albumsMap.has(payload.album)) {
                        albumsMap.set(payload.album, {
                            album: payload.album,
                            genre: payload.genre,
                            bpm: payload.bpm,
                            pulse_compas: payload.pulse_compas ?? 4,
                        });
                    }
                }

                offset = data.result.next_page_offset ?? null;
            } while (offset !== null);

            const albums = Array.from(albumsMap.values());

            if (isDevelopment) {
                console.log('[Qdrant] Found albums:', albums.length);
            }

            return albums;
        } catch (error) {
            if (isDevelopment) { console.error('[Qdrant] Error fetching albums:', error); }
            throw new Error(`Failed to fetch albums: ${(error as Error).message}`);
        }
    }

    /**
     * Get all albums for a specific style group
     * Fans out parallel queries for each sub-genre in the group and merges results
     */
    async getAlbumsByStyleGroup(groupName: string): Promise<AlbumInfo[]> {
        const genres = STYLE_GROUPS[groupName];
        if (!genres || genres.length === 0) {
            throw new Error(`Unknown style group: ${groupName}`);
        }

        if (isDevelopment) {
            console.log('[Qdrant] Fetching albums for style group:', groupName, 'genres:', genres);
        }

        // Fan out: one query per sub-genre in parallel.
        // Each individual query is wrapped in .catch(() => []) so a missing sub-genre
        // (genre not in DB, or Qdrant returns 0 results) is silently skipped.
        const results = await Promise.all(
            genres.map(genre =>
                this.getAlbumsByGenre(genre).catch(err => {
                    if (isDevelopment) {
                        console.warn(`[Qdrant] Sub-genre "${genre}" returned no results:`, err);
                    }
                    return [] as AlbumInfo[];
                })
            )
        );

        // Merge and deduplicate by album name
        const albumsMap = new Map<string, AlbumInfo>();
        for (const albumList of results) {
            for (const album of albumList) {
                if (!albumsMap.has(album.album)) {
                    albumsMap.set(album.album, album);
                }
            }
        }

        const merged = Array.from(albumsMap.values());

        // Group-level failure: all sub-genres returned nothing
        if (merged.length === 0) {
            throw new Error(`No albums found for style group: ${groupName}`);
        }

        if (isDevelopment) {
            console.log('[Qdrant] Found', merged.length, 'albums for style group:', groupName);
        }

        return merged;
    }

    /**
     * Get all samples for a specific album
     */
    async getSamplesByAlbum(album: string): Promise<SampleInfo[]> {
        try {
            if (isDevelopment) {
                console.log('[Qdrant] Fetching samples for album:', album);
            }

            let offset: string | null = null;
            const samples: SampleInfo[] = [];

            do {
                const data = await this._scrollFetch(COLLECTIONS.METADATA, {
                    filter: {
                        must: [{ key: 'album', match: { value: album } }],
                    },
                    limit: QUERY_LIMITS.SAMPLES,
                    offset,
                    with_payload: ['file', 'album', 'genre', 'bpm', 'token', 'duration', 'is_fill', 'pulse_compas'],
                    with_vector: false,
                });

                if (!data.result || !data.result.points) {
                    break;
                }

                for (const point of data.result.points) {
                    const payload = point.payload ?? {};
                    samples.push({
                        file: payload.file,
                        album: payload.album,
                        genre: payload.genre,
                        bpm: payload.bpm,
                        token: payload.token,
                        duration: payload.duration,
                        isFill: payload.is_fill === true,
                        pulse_compas: payload.pulse_compas ?? 4,
                    });
                }

                offset = data.result.next_page_offset ?? null;
            } while (offset !== null);

            if (samples.length === 0) {
                throw new Error('No samples found for album');
            }

            if (isDevelopment) {
                console.log('[Qdrant] Found samples:', samples.length);
            }

            return samples;
        } catch (error) {
            if (isDevelopment) { console.error('[Qdrant] Error fetching samples:', error); }
            throw new Error(`Failed to fetch samples: ${(error as Error).message}`);
        }
    }

    /**
     * Get audio data for specific files and album
     * Uses 'should' filter for OR logic (correct Qdrant syntax)
     */
    async getAudioByFilesAndAlbums(filesAndAlbums: { file: string; album: string }[]): Promise<AudioPayload[]> {
        try {
            if (isDevelopment) {
                console.log('[Qdrant] Fetching audio for file+album pairs:', filesAndAlbums);
            }

            const data = await this._scrollFetch(COLLECTIONS.AUDIO, {
                filter: {
                    should: filesAndAlbums.map(fa => ({
                        must: [
                            { key: 'file', match: { value: fa.file } },
                            { key: 'album', match: { value: fa.album } },
                        ],
                    })),
                },
                limit: filesAndAlbums.length,
                with_payload: ['file', 'album', 'audio_data', 'sample_rate', 'n_samples', 'dtype', 'compression', 'encoding'],
                with_vector: false,
            });

            if (!data.result?.points?.length) {
                throw new Error('No audio data found for files+albums');
            }

            const audioPayloads: AudioPayload[] = data.result.points.map((point: any) => point.payload as AudioPayload);

            if (isDevelopment) {
                console.log('[Qdrant] Retrieved audio payloads:', audioPayloads.length);
            }

            return audioPayloads;
        } catch (error) {
            if (isDevelopment) { console.error('[Qdrant] Error fetching audio:', error); }
            throw new Error(`Failed to fetch audio: ${(error as Error).message}`);
        }
    }
}
